import type { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { diagnostics } from './DiagnosticService';
import type { HttpEventInput } from './types';

interface DiagAxiosConfig extends InternalAxiosRequestConfig {
  __diagStart?: number;
}

/** Max chars for request body in diagnostic events.
 *  Prevents large file uploads or GQL responses from bloating the buffer. */
const MAX_BODY_CHARS = 4000;

/** Safely truncate a request/response body for diagnostic capture.
 *  Handles string (JSON.stringify'd by axios), objects, and undefined. */
function safeTruncate(data: unknown): unknown {
  if (data == null) return undefined;
  if (typeof data === 'string') {
    return data.length > MAX_BODY_CHARS
      ? `${data.slice(0, MAX_BODY_CHARS)}…(truncated)`
      : data;
  }
  // Objects — serialize and truncate
  try {
    const json = JSON.stringify(data);
    if (json.length > MAX_BODY_CHARS) {
      return JSON.parse(`${json.slice(0, MAX_BODY_CHARS)}"}}`);
    }
    return data;
  } catch {
    return '(unserializable)';
  }
}

/**
 * Attach diagnostic interceptors to an Axios instance.
 *
 * Uses unshift to prepend interceptors so they run BEFORE any existing
 * response interceptors that might strip the AxiosResponse wrapper
 * (e.g. `response => response.data`).
 */
export function attachDiagnostics(
  instance: AxiosInstance,
  label: string,
): void {
  // Request: stamp start time
  instance.interceptors.request.use((config: DiagAxiosConfig) => {
    if (diagnostics.running) {
      config.__diagStart = Date.now();
    }
    return config;
  });

  // Response: prepend via handlers array so we see the full AxiosResponse
  // before other interceptors strip it to response.data
  const diagResponseFulfilled = (response: any) => {
    if (diagnostics.running && response?.config) {
      const config = response.config as DiagAxiosConfig;
      diagnostics.send({
        t: 'http',
        label,
        method: config.method?.toUpperCase(),
        url: config.url,
        status: response.status,
        req: safeTruncate(config.data),
        res: response.data,
        d: Date.now() - (config.__diagStart || Date.now()),
      } satisfies HttpEventInput);
    }
    return response;
  };

  const diagResponseRejected = (error: any) => {
    if (diagnostics.running) {
      const config = (error?.config || {}) as DiagAxiosConfig;
      diagnostics.send({
        t: 'http',
        label,
        method: config.method?.toUpperCase(),
        url: config.url,
        status: error?.response?.status,
        err: error?.message,
        req: safeTruncate(config.data),
        res: error?.response?.data,
        d: Date.now() - (config.__diagStart || Date.now()),
      } satisfies HttpEventInput);
    }
    return Promise.reject(error);
  };

  // Prepend: interceptors run in order, so unshift makes ours run first
  (instance.interceptors.response as any).handlers.unshift({
    fulfilled: diagResponseFulfilled,
    rejected: diagResponseRejected,
    synchronous: false,
    runWhen: null,
  });
}
